import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { LearningSession } from '../types'

interface SessionResultScreenProps {
  session: LearningSession
  courseId: string
}

/** Durée de la session en minutes, ou null si elle n'est pas déterminable. */
function durationInMinutes(startedAt: string | null, endedAt: string | null): number | null {
  if (!startedAt || !endedAt) return null

  return Math.round((new Date(endedAt).getTime() - new Date(startedAt).getTime()) / 60000)
}

export function SessionResultScreen({ session, courseId }: Readonly<SessionResultScreenProps>) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [xpDisplayed, setXpDisplayed] = useState(0)
  const xpEarned = session.messageCount * 5
  const minutes = durationInMinutes(session.startedAt, session.endedAt)

  let duration = '--'
  if (minutes !== null) {
    duration = minutes < 1 ? t('session.duration_under_minute') : t('session.duration_minutes', { count: minutes })
  }

  useEffect(() => {
    let start = 0
    const step = Math.max(1, Math.ceil(xpEarned / 30))
    const timer = setInterval(() => {
      start += step
      if (start >= xpEarned) {
        setXpDisplayed(xpEarned)
        clearInterval(timer)
      } else {
        setXpDisplayed(start)
      }
    }, 40)
    return () => clearInterval(timer)
  }, [xpEarned])

  return (
    <div className="min-h-screen bg-surface flex flex-col items-center justify-center px-4 py-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[600px] h-[300px] bg-gradient-to-b from-secondary/10 to-transparent rounded-full blur-3xl -z-10" />

      <div className="w-full max-w-sm">
        <div className="bg-white rounded-3xl shadow-[0px_8px_40px_rgba(0,0,0,0.08)] overflow-hidden">
          {/* Gradient header */}
          <div className="bg-gradient-to-r from-primary to-secondary p-6 text-white text-center">
            <div className="text-4xl mb-2">🎉</div>
            <h1 className="font-display text-2xl font-bold">{t('session.result_title')}</h1>
            <p className="text-sm opacity-80 mt-1">{t('session.result_subtitle')}</p>
          </div>

          <div className="p-6">
            {/* Animated XP counter */}
            <div className="bg-secondary/5 rounded-2xl p-5 text-center mb-4 border border-secondary/10">
              <p className="text-xs text-secondary font-semibold uppercase tracking-wider mb-1">{t('session.xp_earned')}</p>
              <p className="font-display text-4xl font-bold text-secondary tabular-nums">+{xpDisplayed}</p>
            </div>

            {/* Stats row */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div className="bg-surface-low rounded-2xl p-4 text-center">
                <p className="font-display text-xl font-bold text-on-surface tabular-nums">{duration}</p>
                <p className="text-xs text-on-surface-variant mt-0.5">{t('session.duration')}</p>
              </div>
              <div className="bg-surface-low rounded-2xl p-4 text-center">
                <p className="font-display text-xl font-bold text-on-surface tabular-nums">{session.messageCount}</p>
                <p className="text-xs text-on-surface-variant mt-0.5">{t('session.exchanges')}</p>
              </div>
            </div>

            {/* Info message */}
            <p className="text-xs text-on-surface-variant text-center mb-6 px-2">
              {t('session.concepts_pending')}
            </p>

            {/* Actions */}
            <div className="flex flex-col gap-2">
              <button
                type="button"
                onClick={() => navigate(`/courses/${courseId}/progress`)}
                className="w-full rounded-full bg-primary px-4 py-3 text-sm font-semibold text-white hover:opacity-90 transition-opacity"
              >
                {t('session.see_progress')}
              </button>
              <button
                type="button"
                onClick={() => navigate('/courses')}
                className="w-full rounded-full bg-surface-low px-4 py-3 text-sm font-medium text-on-surface hover:bg-surface-container transition-colors"
              >
                {t('session.back_to_courses')}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
